<template>
  <el-drawer
    title="用户信息详情"
    :visible.sync="visible"
    direction="rtl"
    size="60%"
    :before-close="handleClose"
    custom-class="user-info-drawer"
  >
    <div class="drawer-body">
      <!-- 1. 基本信息复现 -->
      <section class="drawer-section">
        <h4>基本信息</h4>
        <dl class="kv-grid">
          <dt>用户名</dt><dd>{{ row.userName | dash }}</dd>
          <dt>手机号</dt><dd>{{ row.telNum | dash }}</dd>
          <dt>用户ID</dt><dd>{{ row.userId | dash }}</dd>
          <dt>游戏</dt><dd>{{ row.appname | dash }}</dd>
          <dt>appkey</dt><dd>{{ row.appkey | dash }}</dd>
          <dt>角色名称</dt><dd>{{ row.roleName | dash }}</dd>
          <dt>角色ID</dt><dd>{{ row.roleId | dash }}</dd>
          <dt>区服</dt><dd>{{ serverDisplay }}</dd>
          <dt>实名信息</dt><dd>{{ row.realName | dash }}</dd>
          <dt>注册时间</dt><dd>{{ row.registerTimeDisplay | dash }}</dd>
          <dt>登录时间区间</dt>
          <dd>
            <HighlightText :text="loginRangeRichText" variant="tag" />
          </dd>
          <dt>充值时间区间</dt>
          <dd>
            <HighlightText :text="rechargeRangeRichText" variant="tag" />
          </dd>
          <dt>充值内容</dt><dd>{{ purchasedDisplay }}</dd>
          <dt>充值金额（所有）</dt><dd>{{ row.rechargeTotalAll | dash }}</dd>
          <dt>充值金额（条件）</dt><dd>{{ row.rechargeTotalByRole | dash }}</dd>
          <dt>充值金额（双周）</dt><dd>{{ row.rechargeTotalBiweekly | dash }}</dd>
          <dt>平台</dt><dd>{{ platformText }}</dd>
        </dl>
      </section>

      <!-- 2. 设备列表（来自 row.deviceLoginRecordsTop5） -->
      <section class="drawer-section">
        <h4>设备列表 </h4>
        <el-empty v-if="deviceTop5.length === 0" description="暂无设备数据" :image-size="60"></el-empty>
        <ul v-else class="device-list">
          <li v-for="(d, idx) in deviceTop5" :key="idx">
            <strong>{{ d.deviceType || '-' }}</strong>
            <span class="sep"> — </span>
            <span>{{ d.idLabel }} {{ d.deviceId || '****' }}</span>
            <span class="sep"> · </span>
            <span>最近登录 {{ d.loginTimeText }}</span>
          </li>
        </ul>
      </section>

      <!-- 3. 异常登录记录 -->
      <section class="drawer-section">
        <h4>异常登录记录</h4>
        <el-table :data="abnormalLogins" border size="mini" empty-text="暂无异常登录">
          <el-table-column label="异常记录" width="120">
            <template slot-scope="scope">
              <HighlightText :text="scope.row.tagRich" variant="tag" />
            </template>
          </el-table-column>
          <el-table-column prop="loginTime" label="异常登录时间"></el-table-column>
        </el-table>
      </section>

      <!-- 4. 异常充值记录 -->
      <section class="drawer-section">
        <h4>异常充值记录</h4>
        <el-table :data="abnormalRecharges" border size="mini" empty-text="暂无异常充值">
          <el-table-column label="异常记录" width="120">
            <template slot-scope="scope">
              <HighlightText :text="scope.row.tagRich" variant="tag" />
            </template>
          </el-table-column>
          <el-table-column prop="payTime" label="异常充值时间"></el-table-column>
        </el-table>
      </section>

      <!-- 6. 未成年聊天情况 -->
      <section class="drawer-section">
        <h4>未成年聊天情况</h4>
        <el-alert
          v-if="chatDegradeMsg"
          :title="chatDegradeMsg"
          type="warning"
          show-icon
          :closable="false"
          style="margin-bottom: 8px;"
        ></el-alert>
        <el-table
          v-if="!chatDegradeMsg"
          :data="chatRecords"
          border
          size="mini"
          empty-text="未命中关键词或暂无聊天记录"
        >
          <el-table-column prop="typeWord" label="聊天类型" width="100"></el-table-column>
          <el-table-column prop="roleName" label="角色名" width="120"></el-table-column>
          <el-table-column label="聊天信息">
            <template slot-scope="scope">
              <HighlightText :text="scope.row.message" variant="mark" />
            </template>
          </el-table-column>
          <el-table-column prop="chatAt" label="聊天时间" width="160"></el-table-column>
        </el-table>
      </section>
    </div>
  </el-drawer>
</template>

<script>
import HighlightText from './HighlightText.vue';
import PayQualCell from './PayQualCell.vue';
import {
  getAbnormalDeviceLoginRecords,
  getDeviceLoginRecords,
  getPlatformText,
} from './utils';

export default {
  name: 'UserDrawer',
  components: { HighlightText, PayQualCell },
  filters: {
    dash(v) {
      if (v == null || v === '') return '-';
      return v;
    },
  },
  data() {
    return {
      visible: false,
      row: {},
    };
  },
  computed: {
    serverDisplay() {
      const parts = [this.row.serverId, this.row.serverName].filter(Boolean);
      return parts.join(' / ') || '-';
    },
    purchasedDisplay() {
      const arr = this.row.purchasedGoodsNames;
      return Array.isArray(arr) && arr.length > 0 ? arr.join('、') : '-';
    },
    platformText() {
      return getPlatformText(this.row);
    },
    deviceTop5() {
      const list = getDeviceLoginRecords(this.row);
      return list.map(d => {
        const isIpad = /ipad/i.test(d.deviceType || '');
        return {
          ...d,
          idLabel: isIpad ? '序列号尾号' : 'IMEI 尾号',
          loginTimeText: d.loginTime || '-',
        };
      });
    },
    // 登录时间区间富文本：服务端 loginTimeRange 自带 {{}} 标记
    loginRangeRichText() {
      return this.row.loginTimeRange || '-';
    },
    // 充值时间区间富文本：服务端 payTimeRange 自带 {{}} 标记
    rechargeRangeRichText() {
      return this.row.payTimeRange || '-';
    },
    // 异常充值记录：来源 row.userRecentPayRecords
    // 每条结构：{ payTime, gameId, timeMark }，平铺前 5 条
    abnormalRecharges() {
      const list = this.row.userRecentPayRecords;
      if (!Array.isArray(list)) return [];
      return list.slice(0, 5).map(item => ({
        tagRich: item.timeMark ? `{{${item.timeMark}}}` : '',
        payTime: item.payTime || '-',
      }));
    },
    // 异常登录记录：使用服务端异常登录 Top5
    // 标签来自服务端字段 loginTimeMark（如 "上学"/"凌晨"），无值则不显示标签
    abnormalLogins() {
      const list = getAbnormalDeviceLoginRecords(this.row);
      return list.map(d => ({
        tagRich: d.loginTimeMark ? `{{${d.loginTimeMark}}}` : '',
        loginTime: d.loginTime || '-',
      }));
    },
    chatRecords() {
      return (this.row.chats && this.row.chats.chat) || [];
    },
    chatDegradeMsg() {
      const desc = this.row.chats && this.row.chats.desc;
      if (!desc) return '';
      if (/暂不可用|无天网|无权限|降级/.test(desc) && (!this.chatRecords || this.chatRecords.length === 0)) {
        return desc;
      }
      return '';
    },
  },
  methods: {
    open(row) {
      this.row = row || {};
      this.visible = true;
    },
    handleClose(done) {
      this.visible = false;
      done && done();
    },
  },
};
</script>

<style lang="less" scoped>
.drawer-body {
  padding: 0 20px 24px;
}
.drawer-section {
  margin-bottom: 22px;
  h4 {
    margin: 12px 0 8px;
    padding-left: 8px;
    border-left: 3px solid #1890ff;
    font-size: 14px;
    .hint {
      color: #8c8c8c;
      font-size: 12px;
      font-weight: normal;
      margin-left: 6px;
    }
  }
}
.kv-grid {
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 6px 8px;
  font-size: 12px;
  margin: 0;
  dt {
    color: #8c8c8c;
  }
  dd {
    margin: 0;
    word-break: break-all;
  }
}
.hint {
  color: #8c8c8c;
  font-size: 12px;
}
.device-list {
  list-style: none;
  margin: 0;
  padding: 0;
  font-size: 12px;
  li {
    padding: 10px 12px;
    border: 1px solid #e8e8e8;
    border-radius: 4px;
    margin-bottom: 8px;
    line-height: 1.5;
  }
  .sep {
    color: #bfbfbf;
    margin: 0 2px;
  }
  strong {
    color: #262626;
  }
}
</style>
